import React, { useContext } from 'react';
import useSWR from 'swr';

import RetailerContext from '../context';
import { Stack } from '@mui/material';
import constants from '../utils/constants';
import { fetcher } from '../utils/fetcher';
import Videos from './Videos';

function normalizeVideoSource(source) {
  return (source || '').toUpperCase();
}

function pickSmartRender(asset, retailer) {
  const sorted = asset?.renders?.slice()?.sort((a, b) => b.id - a.id) || [];
  if (!sorted.length) return null;
  const byBrand = sorted.find((r) => r.brand === retailer?.smart_videos_brand);
  return byBrand || null;
}

function directusFileIdFromVideoField(videoRecord) {
  const v = videoRecord.video;
  if (v != null && typeof v === 'object' && v.id) return v.id;
  if (typeof v === 'string' && v) return v;
  return null;
}

function directusAssetUrl(fileId) {
  if (!fileId) return null;
  const base = process.env.REACT_APP_API_URL || '';
  const token = process.env.REACT_APP_ACCESS_TOKEN;
  const qs = token ? `?access_token=${encodeURIComponent(token)}` : '';
  return `${base}/assets/${fileId}${qs}`;
}

function videoRecordToEmbedItem(videoRecord, retailer) {
  const sourceNorm = normalizeVideoSource(videoRecord.source);

  const item = {
    id: videoRecord.id,
    videoType:
      sourceNorm === 'YOUTUBE'
        ? 'youtube'
        : sourceNorm === 'SMART'
          ? 'smart'
          : sourceNorm === 'FILE'
            ? 'file'
            : 'vimeo',
  };

  if (sourceNorm === 'FILE') {
    item.sourceUrl = directusAssetUrl(directusFileIdFromVideoField(videoRecord));
  }

  if (sourceNorm === 'SMART') {
    if (videoRecord.smart_asset != null) {
      const render = pickSmartRender(videoRecord.smart_asset, retailer);

      if (render?.file_id) {
        const baseUrl = process.env.REACT_APP_SMART_VIDEO_BASE_URL || 'https://smart-video.nyc3.digitaloceanspaces.com/uploads';
        item.sourceUrl = `${baseUrl}/${render.file_id}.mp4`;
      }
    }
    if (!item.sourceUrl && videoRecord.smart_video?.url) {
      item.sourceUrl = videoRecord.smart_video.url;
    }
  }

  if (!item.sourceUrl && videoRecord.video_id && sourceNorm !== 'FILE') {
    if (sourceNorm === 'VIMEO') {
      const embedId = String(videoRecord.video_id).replace('https://vimeo.com/', '');
      item.sourceUrl = `${constants.VIMEO_PLAYER_URL}/${embedId}`;
    }

    if (sourceNorm === 'YOUTUBE') {
      item.sourceUrl = `${constants.YOUTUBE_PLAYER_URL}/${videoRecord.video_id}`;
    }
  }

  return item;
}

function VideoGallery({ videos = [], mode = 'light' }) {
  const { retailer } = useContext(RetailerContext);

  const { data: fetchedVideoRecords = [], isLoading: isLoadingVideos } = useSWR(
    videos.length > 0
      ? {
          url: 'videos',
          filter: {
            _and: [
              { id: { _in: videos } },
              {
                _or: [
                  { status: { _eq: 'published' } },
                  { status: { _eq: 'Published' } },
                ],
              },
            ],
          },
          fields: '*,smart_asset.*,smart_asset.renders.*,smart_video.*,video.*',
        }
      : null,
    fetcher
  );

  const videosToShow = fetchedVideoRecords?.map(video => videoRecordToEmbedItem(video, retailer)).filter(video => !!video.sourceUrl) || [];

  if (isLoadingVideos) return null;

  return (
    <Stack sx={{ my: 3 }}>
      <Videos
        sx={{ p: 0 }}
        mode={mode || 'light'}
        videos={videosToShow}
      />
    </Stack>
  );
}

export default VideoGallery;
